import { FC } from "react";
import { useTranslation } from "react-i18next";
import { useIonRouter } from "@ionic/react";
import { useSelector } from "react-redux";

import { RibbonTier, RibbonVM } from "../../pages/venue/ribbonTypes";
import {
    formatMetric,
    formatMetricValue,
    METRIC_SECTIONS,
    metricLabel,
} from "../../pages/venue/leaderboardModel";
import { RootState } from "../../redux/store";
import { trackEvent } from "../../services/analytics/analyticsService";
import {
    LEADERBOARD_RIBBON_TAPPED,
    LeaderboardTapContext,
    ribbonTapProps,
} from "../../pages/venue/leaderboardAnalytics";
import "./venueRibbons.css";

const KEY = "leaderboards.Ribbon.";

/**
 * One contextual record ribbon, rendered directly beneath the metric card that earned it
 * (SCORZA-739 design handoff, SPEC §2).
 *
 * A ribbon is a real button, not a decorated div: it navigates, so it has to be reachable by
 * keyboard and announce itself to a screen reader. The tier colour is decorative — every state
 * also says in words what happened, because colour alone would leave "all-time record" and
 * "matched the record" indistinguishable.
 */

/** Tier → the emoji and colour class the design assigns it. */
const TIER_STYLE: Record<RibbonTier, { icon: string; variant: string }> = {
    firstHere: { icon: "🏁", variant: "gold" },
    allTimeRecord: { icon: "🏆", variant: "gold" },
    matched: { icon: "⚡", variant: "green" },
    monthlyFirst: { icon: "🥇", variant: "gold" },
    onBoard: { icon: "🥉", variant: "green" },
    personalBest: { icon: "📈", variant: "green" },
    personalRank: { icon: "📈", variant: "green" },
    chasing: { icon: "🎯", variant: "chase" },
};

export interface RecordRibbonProps {
    ribbon: RibbonVM | null;
    /** The venue's display name, when we know it — several tiers name the ground. */
    venueName?: string | null;
    /** Where tapping the ribbon goes, minus the section anchor. */
    venueRoute: string;
    analytics: LeaderboardTapContext;
    testId?: string;
}

export const RecordRibbon: FC<RecordRibbonProps> = ({
    ribbon,
    venueName,
    venueRoute,
    analytics,
    testId,
}) => {
    const { t } = useTranslation();
    const router = useIonRouter();
    const { useImperial } = useSelector(
        (root: RootState) => root.settings.settings
    );

    // Tier 6 in the design ladder: nothing worth saying about this metric, so the metric card
    // sits on its own. The section survives via the other ribbons or the Local Legend entry.
    if (!ribbon) {
        return null;
    }

    const { icon, variant } = TIER_STYLE[ribbon.tier];
    const metric = metricLabel(ribbon.metric, t);
    const value = formatMetricValue(
        ribbon.metric,
        ribbon.value,
        useImperial,
        t
    );
    const gap =
        ribbon.gap === undefined
            ? ""
            : formatMetric(ribbon.metric, ribbon.gap, useImperial, t);

    const named = !!venueName;
    let tag: string;
    let sub: string;

    switch (ribbon.tier) {
        case "firstHere":
            tag = t(KEY + "firstHereTag", { metric });
            sub = t(KEY + "firstHereSub");
            break;
        case "allTimeRecord":
            tag = t(KEY + "allTimeRecordTag", { metric });
            sub = named
                ? t(KEY + "allTimeRecordSub", { venue: venueName })
                : t(KEY + "allTimeRecordSubUnnamed");
            break;
        case "matched":
            tag = t(KEY + "matchedTag", { metric });
            sub = t(KEY + "matchedSub");
            break;
        case "monthlyFirst":
            tag = t(KEY + "monthlyFirstTag", { metric });
            sub = t(KEY + "monthlyFirstSub");
            break;
        case "onBoard":
            tag = t(KEY + "onBoardTag", {
                rank: ribbon.boardRank,
                metric,
            });
            sub = venueName
                ? t(KEY + "onBoardSub", { venue: venueName })
                : t(KEY + "onBoardSubUnnamed");
            break;
        case "personalBest":
            tag = t(KEY + "personalBestTag", { metric });
            sub = named
                ? t(KEY + "personalBestSub", { venue: venueName })
                : t(KEY + "personalBestSubUnnamed");
            break;
        case "personalRank":
            // Ordinals do not survive interpolation in any language worth shipping, so 2nd and
            // 3rd are separate keys rather than "{{rank}}th".
            tag = t(
                KEY +
                    (ribbon.personalRank === 2
                        ? "personalRankSecondTag"
                        : "personalRankThirdTag"),
                { metric }
            );
            sub = t(KEY + "personalRankSub", { gap });
            break;
        case "chasing":
            tag = t(KEY + "chasingTag", { metric });
            sub = t(KEY + "chasingSub", { gap });
            break;
    }

    const label = `${tag}. ${sub}. ${t(KEY + "action")}`;

    return (
        <button
            type="button"
            className={`venue-ribbon venue-ribbon-${variant}`}
            data-testid={testId || `venue-ribbon-${ribbon.metric}`}
            data-tier={ribbon.tier}
            aria-label={label}
            onClick={() => {
                trackEvent(
                    LEADERBOARD_RIBBON_TAPPED,
                    ribbonTapProps(ribbon, analytics, useImperial)
                );
                router.push(
                    `${venueRoute}&section=${METRIC_SECTIONS[ribbon.metric]}`,
                    "forward"
                );
            }}
        >
            <span className="venue-ribbon-icon" aria-hidden="true">
                {icon}
            </span>
            <div className="venue-ribbon-text">
                <h2 className="venue-ribbon-tag">{tag}</h2>
                <span className="venue-ribbon-sub">{sub}</span>
                {ribbon.progress !== undefined && (
                    <span className="venue-ribbon-progress">
                        <span
                            className="venue-ribbon-progress-fill"
                            style={{
                                width: `${Math.round(ribbon.progress * 100)}%`,
                            }}
                        />
                    </span>
                )}
            </div>
            <span className="venue-ribbon-value">
                {value.value}
                {value.unit && (
                    <span className="venue-ribbon-unit"> {value.unit}</span>
                )}
            </span>
            <span className="venue-ribbon-arrow" aria-hidden="true">
                →
            </span>
        </button>
    );
};
